import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AuthPrompt from './AuthPrompt';
import { useSiteContent } from '../contexts/SiteContentContext';
import { PLAYBOOK_TYPES } from '../types/picks';
import { fetchHiddenOdikaiTypes } from '../utils/playbookVisibility';
import { isSuperAdminEmail } from '../constants/admin';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import Pick, { PickListHeader } from './Pick';
import SortButton from './SortButton';
import PlaybookCard from './PlaybookCard';
import PlaybookComparison from './PlaybookComparison';
import FightCard from './FightCard';
import Collapsible from './Collapsible';
import { fetchUpcomingEvents } from '../utils/upcomingEvents';

// Format number with commas and decimal places
const formatCurrency = (number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number);
};

// Format date string to readable format
const formatDate = (dateString, includeTime = false) => {
  if (!dateString) return 'N/A';
  
  // Handle Firestore Timestamp objects
  if (dateString && typeof dateString === 'object' && dateString.toDate) {
    const date = dateString.toDate();
    if (includeTime) {
      return `${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      })} @ ${date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).toLowerCase()}`;
    }
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  // Handle regular date strings. A bare "YYYY-MM-DD" is a calendar day: read it as local, not UTC midnight,
  // which in US time zones would show the day before.
  const dayOnly = typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const date = dayOnly
    ? new Date(Number(dateString.slice(0, 4)), Number(dateString.slice(5, 7)) - 1, Number(dateString.slice(8, 10)))
    : new Date(dateString);
  if (includeTime) {
    return `${date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    })} @ ${date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).toLowerCase()}`;
  }
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
};

const PlaybookSelector = ({ playbook, types, hiddenTypes, description, onChange }) => (
  <div className="control-block">
    <label className="field-label" htmlFor="picks-playbook">Playbook</label>
    <select
      id="picks-playbook"
      value={playbook}
      onChange={onChange}
      className="selector-input"
    >
      {types.map(type => (
        <option key={type} value={type}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
          {hiddenTypes.has(type) ? ' (hidden)' : ''}
        </option>
      ))}
    </select>
    {description && <p className="control-note">{description}</p>}
  </div>
);

const EventSelector = ({ event, events, onChange, lastUpdated }) => (
  <div className="control-block">
    <label className="field-label" htmlFor="picks-event">Event</label>
    <select
      id="picks-event"
      value={event?.id || ''}
      onChange={onChange}
      className="selector-input"
    >
      {events.map(evt => (
        <option key={evt.id} value={evt.id}>{evt.name}</option>
      ))}
    </select>
    {event && (
      <dl className="control-meta">
        <div>
          <dt>Event date</dt>
          <dd>{formatDate(event.date)}</dd>
        </div>
        {lastUpdated && (
          <div>
            <dt>Odds and picks updated</dt>
            <dd>{formatDate(lastUpdated, true)}</dd>
          </div>
        )}
      </dl>
    )}
  </div>
);

const BudgetInput = ({ value, onChange }) => {
  const handleChange = (e) => {
    const cleanValue = e.target.value.replace(/[^\d.]/g, '');
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount <= 1) {
      onChange(cleanValue);
    }
  };

  return (
    <div className="control-block">
      <label className="field-label" htmlFor="picks-budget">Your budget</label>
      <div className="budget-input">
        <span className="currency-symbol">$</span>
        <input
          id="picks-budget"
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
          className="selector-input"
          placeholder="Enter your budget"
        />
      </div>
    </div>
  );
};

const TotalWager = ({ picks, budget }) => {
  const effectiveBudget = parseFloat(budget) || 0;
  if (!picks || picks.length === 0) return null;

  const totalWager = picks.reduce((total, pick) => {
    const amount = pick.budget_amount || pick.allocation || 0;
    return total + (effectiveBudget * amount);
  }, 0).toFixed(2);

  const totalAllocation = picks.reduce((total, pick) => total + (pick.budget_amount || pick.allocation || 0), 0) * 100;

  const totalPotentialWin = picks.reduce((total, pick) => {
    const betAmount = effectiveBudget * (pick.budget_amount || pick.allocation || 0);
    const win = betAmount * (pick.decimal_odds || pick.decimal || 1);
    return total + win;
  }, 0).toFixed(2);

  const returnMultiplier = (parseFloat(totalPotentialWin) / parseFloat(totalWager)).toFixed(2);

  // Each pick's chance of winning comes with the published picks (data job, playbook_builder/pick_chance.py);
  // the expected win is shown only when every pick has one.
  const hasChances = picks.every(pick => typeof pick.win_chance === 'number');
  const expectedWin = hasChances ? picks.reduce((total, pick) => {
    const betAmount = effectiveBudget * (pick.budget_amount || pick.allocation || 0);
    return total + betAmount * (pick.decimal_odds || pick.decimal || 1) * pick.win_chance;
  }, 0) : 0;
  const expectedMultiplier = parseFloat(totalWager) > 0 ? (expectedWin / parseFloat(totalWager)).toFixed(2) : '0.00';
  // The event's 10th-90th percentile return per dollar staked, the same on each of its picks
  const range = hasChances && picks[0].return_range;
  const hasRange = range && typeof range.low === 'number' && typeof range.high === 'number';

  return (
    <div className={`total-wager${hasChances ? ' has-expected' : ''}`}>
      <div>
        <span className="tw-label">Total wager</span>
        <span className="tw-value">${formatCurrency(parseFloat(totalWager))}</span>
        <span className="tw-note">{totalAllocation.toFixed(1)}% of budget</span>
      </div>
      <div>
        <span className="tw-label">Potential win</span>
        <span className="tw-value">${formatCurrency(parseFloat(totalPotentialWin))}</span>
        <span className="tw-note">{returnMultiplier}x the wager</span>
      </div>
      {hasChances && (
        <div title="The average return of these bets over many events like this one: each bet's payout times its estimated chance of winning, from the betting market adjusted by our models. Eight times in ten the return should land in the range shown; the closer to the event the odds were updated, the more reliable both are.">
          <span className="tw-label">Expected win</span>
          <span className="tw-value">${formatCurrency(expectedWin)}</span>
          <span className="tw-note">
            {hasRange
              ? `8 in 10: $${formatCurrency(parseFloat(totalWager) * range.low)}–$${formatCurrency(parseFloat(totalWager) * range.high)}`
              : `${expectedMultiplier}x the wager`}
          </span>
        </div>
      )}
      <div>
        <span className="tw-label">Recommended bets</span>
        <span className="tw-value">{picks.length}</span>
      </div>
    </div>
  );
};

// Largest stake in a list, so each pick's bar is drawn relative to it
const largestWeight = (picks) => picks.reduce(
  (max, pick) => Math.max(max, pick.budget_amount || pick.allocation || 0),
  0
);

export default function Picks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('odikai');
  
  // Get playbook from URL params if present
  const playbookParam = searchParams.get('playbook');
  const initialPlaybook = playbookParam && Object.values(PLAYBOOK_TYPES).includes(playbookParam) 
    ? playbookParam 
    : PLAYBOOK_TYPES.BASELINE;
  
  const [selectedPlaybook, setSelectedPlaybook] = useState(initialPlaybook);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [budget, setBudget] = useState('500');
  const [currentPlaybook, setCurrentPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPlaybooks, setUserPlaybooks] = useState([]);
  const [sortField, setSortField] = useState('budget_amount');
  const [sortDirection, setSortDirection] = useState('desc');
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());
  const playbookDescriptions = useSiteContent().content.playbook_descriptions;

  // Odikai playbooks the admin has hidden; the super admin still sees them, marked hidden
  const isAdmin = isSuperAdminEmail(user);
  const shownTypes = Object.values(PLAYBOOK_TYPES).filter(type => isAdmin || !hiddenTypes.has(type));
  const firstShownType = shownTypes[0] || PLAYBOOK_TYPES.BASELINE;

  useEffect(() => {
    fetchHiddenOdikaiTypes().then(setHiddenTypes);
  }, []);

  // Load the upcoming cards once; the playbook's picks are matched to them by event id
  useEffect(() => {
    fetchUpcomingEvents()
      .then(setUpcomingEvents)
      .catch((error) => console.error('Error fetching upcoming events:', error));
  }, []);

  // Update selected playbook when URL param changes
  useEffect(() => {
    if (playbookParam && Object.values(PLAYBOOK_TYPES).includes(playbookParam)) {
      setSelectedPlaybook(playbookParam);
      setActiveTab('odikai');
    }
  }, [playbookParam]);

  // Handler to update both state and URL when playbook changes
  const handlePlaybookChange = (newPlaybook) => {
    setSelectedPlaybook(newPlaybook);
    // Update URL search params
    const newSearchParams = new URLSearchParams(searchParams);
    if (newPlaybook && Object.values(PLAYBOOK_TYPES).includes(newPlaybook)) {
      newSearchParams.set('playbook', newPlaybook);
    } else {
      newSearchParams.delete('playbook');
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // A hidden playbook (from an old link, say) falls back to the first one still shown
  useEffect(() => {
    if (activeTab === 'odikai' && selectedPlaybook && !isAdmin && hiddenTypes.has(selectedPlaybook)) {
      handlePlaybookChange(firstShownType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenTypes, selectedPlaybook, activeTab, isAdmin]);

  // Fetch Odikai playbook and derive events from its picks (no date filter)
  useEffect(() => {
    async function fetchPlaybookAndEvents() {
      if (activeTab !== 'odikai' || !selectedPlaybook) return;

      setLoading(true);
      try {
        const playbook_id = `odikai_${selectedPlaybook}`;
        const playbookRef = doc(db, 'playbooks', playbook_id);
        const playbookDoc = await getDoc(playbookRef);

        if (playbookDoc.exists()) {
          const playbookData = playbookDoc.data();
          const allPicks = playbookData.picks || [];

          // Store full playbook (all picks); display will filter by selectedEvent
          setCurrentPlaybook({
            id: playbookDoc.id,
            ...playbookData,
            picks: allPicks
          });

          // Every upcoming event, then any event the picks name that is not among them; sorted by date
          const eventMap = new Map();
          upcomingEvents.forEach(evt => {
            eventMap.set(evt.id, { id: evt.id, name: evt.name, date: evt.date, fights: evt.fights || [] });
          });
          allPicks.forEach(pick => {
            if (pick.event && !eventMap.has(pick.event.id)) {
              eventMap.set(pick.event.id, {
                id: pick.event.id,
                name: pick.event.name,
                date: pick.event.date
              });
            }
          });
          const derivedEvents = Array.from(eventMap.values())
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          setEvents(derivedEvents);

          // Preserve selected event if still in list; otherwise select first
          setSelectedEvent(prev => {
            if (!prev || derivedEvents.length === 0) return derivedEvents[0] || null;
            const stillPresent = derivedEvents.find(e => e.id === prev.id);
            return stillPresent || derivedEvents[0] || null;
          });
        } else {
          setCurrentPlaybook(null);
          setEvents([]);
          setSelectedEvent(null);
        }
      } catch (error) {
        console.error('Error fetching playbook:', error);
        setCurrentPlaybook(null);
        setEvents([]);
        setSelectedEvent(null);
      }
      setLoading(false);
    }

    fetchPlaybookAndEvents();
  }, [activeTab, selectedPlaybook, upcomingEvents]);

  // Calculate estimated win for a pick based on budget and decimal odds
  const calculateEstimatedWin = (budgetAmount, decimalOdds) => {
    const effectiveBudget = parseFloat(budget) || 0;
    if (!budgetAmount || !decimalOdds) return '0.00';
    const betAmount = effectiveBudget * (budgetAmount || 0);
    return (betAmount * (decimalOdds || 1)).toFixed(2);
  };

  // Displayed picks: for Odikai, filter full playbook by selected event; for My Playbooks, use playbook picks as-is
  const picks = activeTab === 'odikai'
    ? (currentPlaybook?.picks || []).filter(pick => pick.event?.id === selectedEvent?.id)
    : (currentPlaybook?.picks || []);

  // Function to handle selecting a user playbook
  const handleSelectUserPlaybook = (playbook) => {
    setCurrentPlaybook(playbook);
  };

  // Sort picks based on current sort field and direction
  const sortPicks = (picksToSort) => {
    if (!picksToSort) return [];
    const effectiveBudget = parseFloat(budget) || 0;
    return [...picksToSort].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'budget_amount':
          aValue = effectiveBudget * (a.budget_amount || a.allocation || 0);
          bValue = effectiveBudget * (b.budget_amount || b.allocation || 0);
          break;
        case 'odds':
          aValue = a.decimal_odds || a.decimal || 0;
          bValue = b.decimal_odds || b.decimal || 0;
          break;
        case 'win':
          aValue = calculateEstimatedWin(a.budget_amount || a.allocation || 0, a.decimal_odds || a.decimal || 0);
          bValue = calculateEstimatedWin(b.budget_amount || b.allocation || 0, b.decimal_odds || b.decimal || 0);
          break;
        case 'supporting_strategies':
          aValue = a.supporting_strategies || 0;
          bValue = b.supporting_strategies || 0;
          break;
        default:
          return 0;
      }
      
      return sortDirection === 'desc' ? bValue - aValue : aValue - bValue;
    });
  };

  // Handle sort button click
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  useEffect(() => {
    if (selectedEvent) {
      // ... existing code ...
    }
  }, [selectedEvent, activeTab, user]);

  const sortControls = (showSupport) => (
    <div className="sort-controls" role="group" aria-label="Sort picks">
      <span className="sort-label">Sort</span>
      <SortButton
        field="budget_amount"
        label="Sort by Bet"
        currentField={sortField}
        direction={sortDirection}
        onClick={handleSort}
      />
      <SortButton
        field="odds"
        label="Sort by Odds"
        currentField={sortField}
        direction={sortDirection}
        onClick={handleSort}
      />
      <SortButton
        field="win"
        label="Sort by Win"
        currentField={sortField}
        direction={sortDirection}
        onClick={handleSort}
      />
      {showSupport && (
        <SortButton
          field="supporting_strategies"
          label="Sort by Support"
          currentField={sortField}
          direction={sortDirection}
          onClick={handleSort}
        />
      )}
    </div>
  );

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="header-content">
          <h1>Picks</h1>
          <p className="header-subtitle">Choose a playbook and an event, enter your budget, and get picks sized to it.</p>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-value">{events.length}</span>
            <span className="stat-label">Upcoming events</span>
          </div>
          {activeTab === 'my' && user && (
            <div className="stat-card">
              <span className="stat-value">{userPlaybooks.length}</span>
              <span className="stat-label">My playbooks</span>
            </div>
          )}
        </div>
      </header>

      <div className="picks-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'odikai'}
          className={`picks-tab ${activeTab === 'odikai' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('odikai');
            handlePlaybookChange(firstShownType);
          }}
        >
          Odikai playbooks
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'compare'}
          className={`picks-tab ${activeTab === 'compare' ? 'active' : ''}`}
          onClick={() => setActiveTab('compare')}
        >
          Compare playbooks
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'my'}
          className={`picks-tab ${activeTab === 'my' ? 'active' : ''}`}
          onClick={async () => {
            setLoading(true);
            setActiveTab('my');
            // Clear playbook from URL when switching to "My Playbooks" tab
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('playbook');
            setSearchParams(newSearchParams, { replace: true });
            setSelectedPlaybook(null);

            try {
              if (userPlaybooks.length > 0) {
                setCurrentPlaybook(userPlaybooks[0]);
              } else if (user) {
                const playbooksRef = collection(db, 'playbooks');
                const q = query(
                  playbooksRef,
                  where('is_prebuilt', '==', false),
                  where('user_id', '==', user.uid)
                );

                const querySnapshot = await getDocs(q);
                const playbooks = [];
                querySnapshot.forEach((doc) => {
                  playbooks.push({ id: doc.id, ...doc.data() });
                });

                setUserPlaybooks(playbooks);
                if (playbooks.length > 0) {
                  setCurrentPlaybook(playbooks[0]);
                }
              }
            } catch (error) {
              console.error('Error fetching user playbooks:', error);
              setUserPlaybooks([]);
              setCurrentPlaybook(null);
            } finally {
              setLoading(false);
            }
          }}
        >
          My playbooks
        </button>
      </div>

      {activeTab === 'compare' ? (
        <PlaybookComparison />
      ) : activeTab === 'my' && !user ? (
        <div className="auth-prompt-wrapper">
          <AuthPrompt />
        </div>
      ) : activeTab === 'odikai' ? (
        <div className="picks-layout">
          <aside className="picks-controls">
            <PlaybookSelector
              playbook={selectedPlaybook}
              types={shownTypes}
              hiddenTypes={hiddenTypes}
              description={playbookDescriptions[selectedPlaybook]}
              onChange={(e) => handlePlaybookChange(e.target.value)}
            />
            <EventSelector
              event={selectedEvent}
              events={events}
              onChange={(e) => setSelectedEvent(events.find(event => event.id === e.target.value))}
              lastUpdated={currentPlaybook?.last_updated}
            />
            <BudgetInput
              value={budget}
              onChange={setBudget}
            />
          </aside>

          {budget !== undefined && (
            <section className="picks-main" aria-live="polite">
              <div className="picks-main-head">
                <div>
                  <h2>{selectedEvent?.name || 'Picks'}</h2>
                  {selectedEvent?.date && <p>{formatDate(selectedEvent.date)}</p>}
                </div>
              </div>
              <TotalWager picks={picks} budget={budget} />

              {loading ? (
                <div className="loading"><div className="loading-spinner"></div>Loading picks…</div>
              ) : picks.length > 0 ? (
                <Collapsible
                  title="Picks"
                  meta={`${picks.length} ${picks.length === 1 ? 'bet' : 'bets'}`}
                  className="picks-section"
                >
                {sortControls(Boolean(currentPlaybook?.strategies))}
                <PickListHeader showBudget />
                <div className="picks-grid">
                  {sortPicks(picks).map((pick, index) => (
                    <Pick
                      key={index}
                      pick_name={pick.pick_name || pick.fighter}
                      opponent_name={pick.opponent_name || pick.opponent}
                      type={pick.type || 'Unknown'}
                      weight={pick.budget_amount || pick.allocation}
                      american_odds={pick.american_odds || pick.odds}
                      decimal_odds={pick.decimal_odds || pick.decimal}
                      supporting_strategies={pick.supporting_strategies || 0}
                      total_strategies={currentPlaybook?.strategies || 10}
                      budget={parseFloat(budget || 0)}
                      showBudget={true}
                      maxWeight={largestWeight(picks)}
                    />
                  ))}
                </div>
                </Collapsible>
              ) : (
                <div className="empty-state">
                  {selectedEvent?.fights?.length
                    ? 'No picks for this event yet. Picks are made once betting lines are posted, so check back closer to fight night.'
                    : 'No picks available for this playbook.'}
                </div>
              )}
              {!loading && <FightCard fights={selectedEvent?.fights} picks={picks} />}
            </section>
          )}
        </div>
      ) : (
        <>
          {userPlaybooks.length > 0 && (
            <div className="my-playbooks-picker">
              {userPlaybooks.map((playbook) => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  isActive={currentPlaybook?.id === playbook.id}
                  onClick={() => handleSelectUserPlaybook(playbook)}
                  showActions={false}
                />
              ))}
            </div>
          )}

          {currentPlaybook && (
            <div className="picks-layout">
              <aside className="picks-controls">
                {currentPlaybook.event && (
                  <div className="control-block">
                    <span className="field-label">Event</span>
                    <dl className="control-meta">
                      <div>
                        <dt>Event</dt>
                        <dd>{currentPlaybook.event.name}</dd>
                      </div>
                      <div>
                        <dt>Date</dt>
                        <dd>{formatDate(currentPlaybook.event.date)}</dd>
                      </div>
                    </dl>
                  </div>
                )}
                <BudgetInput
                  value={budget}
                  onChange={setBudget}
                />
              </aside>

              <section className="picks-main">
                <div className="picks-main-head">
                  <div>
                    <h2>{currentPlaybook.name || 'Untitled Playbook'}</h2>
                  </div>
                </div>
                <TotalWager picks={currentPlaybook.picks || []} budget={budget} />
                {sortControls(true)}
                <PickListHeader showBudget />
                <div className="picks-grid">
                  {sortPicks(currentPlaybook.picks || []).map((pick, index) => (
                    <Pick
                      key={index}
                      pick_name={pick.pick_name || pick.fighter || 'Unknown Fighter'}
                      opponent_name={pick.opponent_name || pick.opponent || 'Unknown Opponent'}
                      type={pick.type || 'Unknown'}
                      weight={pick.budget_amount || pick.allocation}
                      american_odds={pick.american_odds || pick.odds}
                      decimal_odds={pick.decimal_odds || pick.decimal}
                      supporting_strategies={pick.supporting_strategies || 0}
                      total_strategies={currentPlaybook.strategies || 10}
                      budget={parseFloat(budget || 0)}
                      showBudget={true}
                      maxWeight={largestWeight(currentPlaybook.picks || [])}
                    />
                  ))}
                </div>
              </section>
            </div>
          )}

          {!loading && userPlaybooks.length === 0 && (
            <div className="empty-state">
              <p>You haven't created any playbooks yet.</p>
              <button className="btn btn-primary" onClick={() => navigate('/playbooks/build')}>Create your first playbook</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
